import { createSlice } from '@reduxjs/toolkit';
import { BrandedLink, BrandedLinkState } from '../../@types/brandedLink';
import axios from '../../utils/axios';
import { dispatch } from '../store';
import { DispatchError } from '../dispatchError';
import { translator } from 'translation/useTranslator';

const initialState: BrandedLinkState = {
  loading: {},
  brandedLinks: []
};

const slice = createSlice({
  name: 'brandedLink',
  initialState,
  reducers: {
    startNewLoading(state, action) {
      state.loading[action.payload] = true;
    },

    endNewLoading(state, action) {
      state.loading[action.payload] = false;
    },

    getBrandedLinksSuccess(state, action) {
      state.loading.brandedLinks = false;
      state.brandedLinks = action.payload || [];
    },

    addBrandedLinkSuccess(state) {
      state.loading.addBrandedLink = false;
    },

    updateBrandedLinkSuccess(state) {
      state.loading.updateBrandedLink = false;
    },

    deleteBrandedLinkSuccess(state) {
      state.loading.deleteBrandedLink = false;
    }
  }
});

export default slice.reducer;

export function getBrandedLinks() {
  dispatch(slice.actions.startNewLoading('brandedLinks'));
  return async () => {
    try {
      const response = await axios.get('/branded-links');
      dispatch(slice.actions.getBrandedLinksSuccess(response.data));
      dispatch(slice.actions.endNewLoading('brandedLinks'));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.brandedLinksFetched() ?? 'Branded links fetched'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('brandedLinks'));
      const defaultErrorMessage =
        translator?.couldNotFetchBrandedLinks() ?? 'Could not fetch branded links';
      throw new DispatchError({ error, defaultErrorMessage });
    }
  };
}

export function addBrandedLink(brandedLink: BrandedLink) {
  dispatch(slice.actions.startNewLoading('addBrandedLink'));
  return async () => {
    try {
      await axios.post('/branded-links', brandedLink);
      dispatch(slice.actions.addBrandedLinkSuccess());
      dispatch(slice.actions.endNewLoading('addBrandedLink'));
      return await Promise.resolve({
        defaultSuccessMessage: translator?.brandedLinkAdded() ?? 'Branded link added'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('addBrandedLink'));
      const defaultErrorMessage =
        translator?.couldNotAddBrandedLink() ?? 'Could not add branded link';
      throw new DispatchError({ error, defaultErrorMessage });
    }
  };
}

export function updateBrandedLink(updatedBrandedLink: BrandedLink) {
  dispatch(slice.actions.startNewLoading('updateBrandedLink'));
  return async () => {
    try {
      await axios.put(`/branded-links/${updatedBrandedLink.id}`, updatedBrandedLink);
      dispatch(slice.actions.updateBrandedLinkSuccess());
      dispatch(slice.actions.endNewLoading('updateBrandedLink'));
      return await Promise.resolve({
        defaultSuccessMessage: translator?.brandedLinkUpdated() ?? 'Branded link updated'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('updateBrandedLink'));
      const defaultErrorMessage =
        translator?.couldNotUpdateBrandedLink() ?? 'Could not update branded link';
      throw new DispatchError({ error, defaultErrorMessage });
    }
  };
}

export function deleteBrandedLink(brandedLinkId: string) {
  dispatch(slice.actions.startNewLoading('deleteBrandedLink'));
  return async () => {
    try {
      await axios.delete(`/branded-links/${brandedLinkId}`);
      dispatch(slice.actions.deleteBrandedLinkSuccess());
      dispatch(slice.actions.endNewLoading('deleteBrandedLink'));
      return await Promise.resolve({
        defaultSuccessMessage: translator?.brandedLinkDeleted() ?? 'Branded link deleted'
      });
    } catch (error: any) {
      dispatch(slice.actions.endNewLoading('deleteBrandedLink'));
      const defaultErrorMessage =
        translator?.couldNotDeleteBrandedLink() ?? 'Could not delete branded link';
      throw new DispatchError({ error, defaultErrorMessage });
    }
  };
}
